import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  TablePagination,
  InputAdornment,
  Divider,
  Tooltip,
  FormHelperText,
  Badge,
  Avatar,
  Fade,
  Grow,
  Zoom,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  MenuBook as MenuIcon,
  SubdirectoryArrowRight as SubcategoryIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Upload as UploadIcon,
  RestaurantMenu as RestaurantIcon,
  Inventory as InventoryIcon,
  ArrowOutward,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  MailOutline as MailOutlineIcon,
  WhatsApp,
  Instagram,
  Email,
  LocationOn,
  Schedule,
  Menu as MenuIconBars,
} from "@mui/icons-material";
import axios from "axios";

/* ------------------------------------------------------------------ */
/*  CONSTANTS — shared brand tokens                                    */
/* ------------------------------------------------------------------ */

const ACCENT = "#d7a66d";
const INK = "#171512";
const MUTED = "#a9a39a";
const HAIRLINE = "rgba(255,255,255,.08)";

const CONTACT = {
  phone: "(+234) 704 100 5087",
  phoneHref: "+2347041005087",
  address: "4th Floor, 1 Bria St, Adetokunbo Ademola Cres, Wuse 2, Abuja",
  email: "info@myelegante.com",
  hours: [{ label: "Mon – Sun", value: "3pm – 2am daily" }],
  socials: {
    instagram:
      "https://www.instagram.com/theterraceabuja?exln=MW9ta3U0djh5ODVhcA%3D%3D&utm_source=qr",
    tiktok: "https://www.tiktok.com/@theterraceabuja?_r=1&_t=ZS-9AnK5AhgUTg",
  },
};

// Optional logo — uncomment and adjust path if you have it in this file's assets
// import logo from "../assets/logo.jpeg";
const logo = null;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

console.debug("[AdminDashboard] Environment loaded - API_BASE_URL:", API_BASE_URL);

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */

const sanitizeInput = (input) => {
  if (!input) return "";
  return input.replace(/[<>]/g, "");
};

const validateItem = (item) => {
  const errors = {};
  if (!item.name?.trim()) errors.name = "Item name is required";
  if (!item.category_id) errors.category_id = "Category is required";
  if (!item.price || parseFloat(item.price) <= 0) errors.price = "Valid price is required";
  if (item.price && parseFloat(item.price) > 999999999) errors.price = "Price is too high";
  if (item.description && item.description.length > 500)
    errors.description = "Description too long (max 500 characters)";
  return errors;
};

const validateCategory = (category) => {
  const errors = {};
  if (!category.name?.trim()) errors.name = "Category name is required";
  if (category.name && category.name.length > 100)
    errors.name = "Category name too long (max 100 characters)";
  return errors;
};

const validateSubcategory = (subcategory) => {
  const errors = {};
  if (!subcategory.name?.trim()) errors.name = "Subcategory name is required";
  if (!subcategory.category_id) errors.category_id = "Category is required";
  if (subcategory.name && subcategory.name.length > 255)
    errors.name = "Subcategory name too long (max 255 characters)";
  return errors;
};

const infoChipSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: "8px",
  background: "rgba(215,166,109,.12)",
  border: "1px solid rgba(215,166,109,.3)",
  flexShrink: 0,
};

const infoLabelSx = {
  fontSize: ".58rem",
  letterSpacing: ".15em",
  color: "#a9a39a",
  lineHeight: 1,
};

/* TikTok inline SVG */
const TikTokIcon = ({ sx }) => (
  <Box component="svg" viewBox="0 0 24 24" fill="currentColor" sx={{ fontSize: 18, ...sx }}>
    <path d="M16.6 5.82a4.28 4.28 0 0 1-3.13-1.36 4.28 4.28 0 0 1-1.16-2.96h-3.1v13.2a2.55 2.55 0 1 1-1.82-2.44V9.1a5.63 5.63 0 1 0 4.92 5.58V8.86a7.3 7.3 0 0 0 4.29 1.38V7.14a4.3 4.3 0 0 1 0-1.32z" />
  </Box>
);

const socialMeta = {
  instagram: { label: "Instagram", icon: <Instagram sx={{ fontSize: 18 }} /> },
  tiktok: { label: "TikTok", icon: <TikTokIcon /> },
};

/* ------------------------------------------------------------------ */
/*  PRELOADER                                                          */
/* ------------------------------------------------------------------ */

function Preloader({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 100) return 100;
        const step = p < 40 ? 3 : p < 75 ? 2 : 1.2;
        return Math.min(100, p + step);
      });
    }, 40);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (progress < 100) return;
    const exitTimer = window.setTimeout(() => setExiting(true), 350);
    const doneTimer = window.setTimeout(() => onDone?.(), 1100);
    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(doneTimer);
    };
  }, [progress, onDone]);

  return (
    <Box
      aria-hidden={exiting}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "#100e0b",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        opacity: exiting ? 0 : 1,
        transform: exiting ? "scale(1.04)" : "scale(1)",
        transition: "opacity 700ms ease, transform 700ms ease",
        pointerEvents: exiting ? "none" : "auto",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(600px 400px at 25% 80%, rgba(215,166,109,.09), transparent 60%),
            radial-gradient(500px 360px at 80% 20%, rgba(215,166,109,.07), transparent 60%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: { xs: 140, md: 180 },
          height: { xs: 140, md: 180 },
          display: "grid",
          placeItems: "center",
          mb: 5,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 200 200"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            animation: "spin 6s linear infinite",
            "@keyframes spin": { to: { transform: "rotate(360deg)" } },
          }}
        >
          <defs>
            <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={ACCENT} stopOpacity="1" />
              <stop offset="60%" stopColor={ACCENT} stopOpacity=".15" />
              <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="88" fill="none" stroke="url(#ringGrad)" strokeWidth="1.5" strokeDasharray="260 340" strokeLinecap="round" />
        </Box>
        <Box
          component="svg"
          viewBox="0 0 200 200"
          sx={{
            position: "absolute",
            inset: 12,
            width: "calc(100% - 24px)",
            height: "calc(100% - 24px)",
            animation: "spinReverse 9s linear infinite",
            "@keyframes spinReverse": { to: { transform: "rotate(-360deg)" } },
          }}
        >
          <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(215,166,109,.25)" strokeWidth="0.8" strokeDasharray="2 6" />
        </Box>

        <Box
          sx={{
            textAlign: "center",
            animation: "pulse 2.4s ease-in-out infinite",
            "@keyframes pulse": {
              "0%,100%": { opacity: 0.9, transform: "scale(1)" },
              "50%": { opacity: 1, transform: "scale(1.03)" },
            },
          }}
        >
          {logo ? (
            <Box
              component="img"
              src={logo}
              alt="logo"
              sx={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: `1px solid ${ACCENT}` }}
            />
          ) : (
            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: { xs: "2rem", md: "2.6rem" },
                letterSpacing: "-.04em",
                lineHeight: 1,
                color: "#fff",
              }}
            >
              T<span style={{ color: ACCENT }}>.</span>
            </Typography>
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: { xs: "1.35rem", md: "1.7rem" },
          letterSpacing: "-.03em",
          color: "#fff",
          mb: 0.8,
        }}
      >
        TERRACE<span style={{ color: ACCENT }}>.</span>
      </Typography>
      <Typography sx={{ fontSize: { xs: ".55rem", md: ".62rem" }, letterSpacing: ".3em", color: "#c9bfae", mb: 5 }}>
        A D M I N · C O N S O L E
      </Typography>

      <Box sx={{ width: { xs: 200, md: 260 }, position: "relative" }}>
        <Box sx={{ height: 2, width: "100%", bgcolor: "rgba(255,255,255,.1)", position: "relative", overflow: "hidden" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${ACCENT}, #f0c58f)`,
              transition: "width 200ms ease",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
          <Typography sx={{ fontSize: ".58rem", letterSpacing: ".22em", color: "#8d867d" }}>
            LOADING DASHBOARD
          </Typography>
          <Typography sx={{ fontSize: ".62rem", color: ACCENT, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  HEADER                                                             */
/* ------------------------------------------------------------------ */

function AdminHeader({ onRefresh, onAddItem, onAddCategory, onAddSubcategory, isMobile }) {
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: "rgba(18,17,15,.94)",
        backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(255,255,255,.12)",
        zIndex: 1100,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 88 }, gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, flexGrow: 1, minWidth: 0 }}>
            {logo && (
              <Box
                sx={{
                  width: { xs: 36, md: 44 },
                  height: { xs: 36, md: 44 },
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "1px solid rgba(215,166,109,.45)",
                  flexShrink: 0,
                }}
              >
                <Box component="img" src={logo} alt="logo" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: "1.25rem", md: "1.65rem" },
                  letterSpacing: "-.04em",
                  lineHeight: 1,
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                TERRACE<span style={{ color: ACCENT }}>.</span>
              </Typography>
              <Typography
                sx={{
                  mt: 0.7,
                  fontSize: { xs: ".5rem", md: ".58rem" },
                  letterSpacing: { xs: ".18em", md: ".25em" },
                  color: "#d2c7b8",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                A D M I N · C O N S O L E
              </Typography>
            </Box>
          </Box>

          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                onClick={onRefresh}
                startIcon={<RefreshIcon />}
                sx={{
                  color: "#eee8df",
                  textTransform: "none",
                  fontSize: ".82rem",
                  "&:hover": { color: ACCENT, bgcolor: "transparent" },
                }}
              >
                Refresh
              </Button>
              <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,.15)", my: 1 }} />
              <Button
                onClick={onAddItem}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: ACCENT,
                  color: "#17120d",
                  borderRadius: 0,
                  px: 2,
                  py: 1,
                  fontSize: ".8rem",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#edc18b" },
                }}
              >
                Add Item
              </Button>
              <Button
                onClick={onAddCategory}
                startIcon={<CategoryIcon />}
                sx={{
                  border: "1px solid rgba(215,166,109,.45)",
                  color: "#f8e9d4",
                  borderRadius: 0,
                  px: 2,
                  py: 1,
                  fontSize: ".8rem",
                  textTransform: "none",
                  "&:hover": { borderColor: ACCENT, bgcolor: "rgba(215,166,109,.08)" },
                }}
              >
                Category
              </Button>
              <Button
                onClick={onAddSubcategory}
                startIcon={<SubcategoryIcon />}
                sx={{
                  border: "1px solid rgba(215,166,109,.45)",
                  color: "#f8e9d4",
                  borderRadius: 0,
                  px: 2,
                  py: 1,
                  fontSize: ".8rem",
                  textTransform: "none",
                  "&:hover": { borderColor: ACCENT, bgcolor: "rgba(215,166,109,.08)" },
                }}
              >
                Subcategory
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}

/* ------------------------------------------------------------------ */
/*  FOOTER                                                             */
/* ------------------------------------------------------------------ */

function InfoRow({ icon, label, children, href }) {
  const content = (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: "6px",
          background: "rgba(215,166,109,.1)",
          border: "1px solid rgba(215,166,109,.28)",
          color: ACCENT,
          flexShrink: 0,
          mt: 0.3,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: ".55rem", letterSpacing: ".18em", color: MUTED, lineHeight: 1, mb: 0.7, fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: ".85rem", color: "#f6f1e9", lineHeight: 1.5, fontWeight: 500, wordBreak: "break-word" }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );

  if (!href) return content;
  return (
    <Box
      component="a"
      href={href}
      sx={{ textDecoration: "none", display: "block", "&:hover .MuiTypography-root": { color: ACCENT } }}
    >
      {content}
    </Box>
  );
}

function AdminFooter() {
  const telHref = `tel:${CONTACT.phoneHref}`;
  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        bgcolor: "#151310",
        color: "#f7f3ed",
        borderTop: `1px solid ${HAIRLINE}`,
        pt: { xs: 6, md: 8 },
        pb: 4,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(600px 300px at 10% 100%, rgba(215,166,109,.06), transparent 60%),
            radial-gradient(500px 260px at 90% 0%, rgba(215,166,109,.05), transparent 60%)
          `,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.1fr 1fr 1fr" },
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* Brand + contact */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.6, mb: 1 }}>
              {logo && (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "1px solid rgba(215,166,109,.45)",
                    flexShrink: 0,
                  }}
                >
                  <Box component="img" src={logo} alt="logo" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </Box>
              )}
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: "1.6rem", md: "1.9rem" },
                  letterSpacing: "-.04em",
                  lineHeight: 1,
                  color: "#fff",
                }}
              >
                TERRACE<span style={{ color: ACCENT }}>.</span>
              </Typography>
            </Box>
            <Typography sx={{ mt: 0.6, fontSize: ".6rem", letterSpacing: ".25em", color: "#d2c7b8", mb: 3 }}>
              L O U N G E · A B U J A
            </Typography>

            <Box sx={{ display: "grid", gap: 2.2 }}>
              <InfoRow icon={<LocationOn sx={{ fontSize: 16 }} />} label="ADDRESS">
                {CONTACT.address}
              </InfoRow>
              <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="CALL US" href={telHref}>
                {CONTACT.phone}
              </InfoRow>
              <InfoRow icon={<Email sx={{ fontSize: 16 }} />} label="EMAIL" href={`mailto:${CONTACT.email}`}>
                {CONTACT.email}
              </InfoRow>
            </Box>
          </Box>

          {/* Opening hours */}
          <Box>
            <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: { xs: "1.5rem", md: "1.7rem" }, letterSpacing: "-.03em", lineHeight: 1.1, mb: 3 }}>
              Opening hours
            </Typography>
            <Box sx={{ border: `1px solid ${HAIRLINE}`, bgcolor: "rgba(255,255,255,.02)", p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Schedule sx={{ fontSize: 18, color: ACCENT }} />
                <Typography sx={{ fontSize: ".58rem", letterSpacing: ".18em", color: MUTED, fontWeight: 600 }}>
                  WEEKLY SCHEDULE
                </Typography>
              </Box>
              {CONTACT.hours.map((h, i) => (
                <Box
                  key={`${h.label}-${i}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    py: 1.4,
                    borderTop: i === 0 ? "none" : `1px solid ${HAIRLINE}`,
                  }}
                >
                  <Typography sx={{ fontSize: ".82rem", color: "#e7e1d8", fontWeight: 500 }}>{h.label}</Typography>
                  <Typography sx={{ fontSize: ".82rem", color: ACCENT, fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {h.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Follow */}
          <Box>
            <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: { xs: "1.5rem", md: "1.7rem" }, letterSpacing: "-.03em", lineHeight: 1.1, mb: 0.6 }}>
              Follow along
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: ".8rem", mb: 3 }}>
              Nights, plates and behind-the-bar moments.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.2, mb: 4 }}>
              {Object.entries(socialMeta).map(([key, meta]) => {
                const href = CONTACT.socials?.[key];
                if (!href) return null;
                return (
                  <IconButton
                    key={key}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={meta.label}
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "8px",
                      color: ACCENT,
                      border: "1px solid rgba(215,166,109,.35)",
                      background: "rgba(215,166,109,.06)",
                      transition: "all .25s ease",
                      "&:hover": {
                        background: ACCENT,
                        color: "#151310",
                        borderColor: ACCENT,
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 22px rgba(215,166,109,.25)",
                      },
                    }}
                  >
                    {meta.icon}
                  </IconButton>
                );
              })}
            </Box>
            <Box sx={{ borderTop: `1px solid ${HAIRLINE}`, pt: 2.5 }}>
              <Typography sx={{ fontSize: ".58rem", letterSpacing: ".18em", color: MUTED, fontWeight: 600 }}>
                Designed by: Eleganté IT dept
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: HAIRLINE }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography sx={{ color: "#8d867d", fontSize: ".78rem" }}>
            © {new Date().getFullYear()} The Terrace Lounge. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            {["Privacy Policy", "Terms of Service"].map((label) => (
              <Typography
                key={label}
                component="a"
                href="#"
                sx={{
                  color: "#8d867d",
                  fontSize: ".78rem",
                  textDecoration: "none",
                  transition: "color .25s ease",
                  "&:hover": { color: ACCENT },
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>

      <Box aria-hidden sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`, opacity: 0.7 }} />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  MAIN ADMIN DASHBOARD                                               */
/* ------------------------------------------------------------------ */

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [menuData, setMenuData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });

  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [subcategorySearchTerm, setSubcategorySearchTerm] = useState("");

  const [itemFormData, setItemFormData] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    subcategory_id: "",
    image: null,
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    icon: "",
    sort_order: "",
  });

  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: "",
    category_id: "",
    image: null,
  });

  const [itemErrors, setItemErrors] = useState({});
  const [categoryErrors, setCategoryErrors] = useState({});
  const [subcategoryErrors, setSubcategoryErrors] = useState({});

  console.debug("[AdminDashboard] Component mounted");
  console.debug("[AdminDashboard] API Base URL:", API_BASE_URL);

  const showSnackbar = useCallback((message, severity = "success") => {
    console.debug(`[AdminDashboard] Snackbar: ${severity} - ${message}`);
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar((prev) => ({ ...prev, open: false })), 6000);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    console.debug("[AdminDashboard] Fetching menu data from:", `${API_BASE_URL}/api_menu_items.php?action=categories`);
    try {
      const response = await axios.get(`${API_BASE_URL}/api_menu_items.php?action=categories`, {
        timeout: 30000,
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      console.debug("[AdminDashboard] Menu data received:", response.data);
      if (response.data.success) {
        setMenuData(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch menu data", "error");
      }
    } catch (error) {
      console.error("[AdminDashboard] Error fetching menu data:", error);
      let errorMsg = "Error fetching menu data: ";
      if (error.code === "ERR_NETWORK") {
        errorMsg += "Cannot connect to server. Please check if the API is running.";
      } else if (error.response?.status === 404) {
        errorMsg += "API endpoint not found. Please check API URL configuration.";
      } else {
        errorMsg += error.response?.data?.message || error.message;
      }
      showSnackbar(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchCategories = useCallback(async () => {
    console.debug("[AdminDashboard] Fetching categories from:", `${API_BASE_URL}/api_categories.php?action=all`);
    try {
      const response = await axios.get(`${API_BASE_URL}/api_categories.php?action=all`, {
        timeout: 30000,
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      console.debug("[AdminDashboard] Categories received:", response.data);
      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        console.warn("[AdminDashboard] Failed to fetch categories:", response.data.message);
      }
    } catch (error) {
      console.error("[AdminDashboard] Error fetching categories:", error);
    }
  }, []);

  const fetchAllSubcategories = useCallback(async () => {
    console.debug("[AdminDashboard] Fetching all subcategories");
    try {
      const allSubs = [];
      for (const category of categories) {
        const response = await axios.get(`${API_BASE_URL}/api_subcategories.php?action=by_category&category_id=${category.id}`, {
          timeout: 30000,
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        });
        if (response.data.success && response.data.data) {
          const subsWithCategory = response.data.data.map((sub) => ({ ...sub, category_name: category.name }));
          allSubs.push(...subsWithCategory);
        }
      }
      console.debug("[AdminDashboard] All subcategories fetched:", allSubs.length);
      setAllSubcategories(allSubs);
    } catch (error) {
      console.error("[AdminDashboard] Error fetching all subcategories:", error);
    }
  }, [categories]);

  const fetchSubcategoriesForCategory = useCallback(async (categoryId) => {
    if (!categoryId) return [];
    console.debug(`[AdminDashboard] Fetching subcategories for category ${categoryId}`);
    try {
      const response = await axios.get(`${API_BASE_URL}/api_subcategories.php?action=by_category&category_id=${categoryId}`, {
        timeout: 30000,
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      console.debug("[AdminDashboard] Subcategories received:", response.data);
      if (response.data.success) return response.data.data;
      return [];
    } catch (error) {
      console.error("[AdminDashboard] Error fetching subcategories:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchData(), fetchCategories()]);
      setLoadingInitial(false);
    };
    init();
  }, [fetchData, fetchCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      fetchAllSubcategories();
    }
  }, [categories, fetchAllSubcategories]);

  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setItemFormData((prev) => ({ ...prev, category_id: categoryId, subcategory_id: "" }));
    await fetchSubcategoriesForCategory(categoryId);
  };

  const handleItemInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files) {
      setItemFormData((prev) => ({ ...prev, image: files[0] }));
      console.debug("[AdminDashboard] Image selected:", files[0].name);
    } else {
      const sanitizedValue = name === "name" || name === "description" ? sanitizeInput(value) : value;
      setItemFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    }
    if (itemErrors[name]) {
      setItemErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = name === "name" ? sanitizeInput(value) : value;
    setCategoryFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    if (categoryErrors[name]) {
      setCategoryErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubcategoryInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files) {
      setSubcategoryFormData((prev) => ({ ...prev, image: files[0] }));
      console.debug("[AdminDashboard] Subcategory image selected:", files[0].name);
    } else {
      const sanitizedValue = name === "name" ? sanitizeInput(value) : value;
      setSubcategoryFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    }
    if (subcategoryErrors[name]) {
      setSubcategoryErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleOpenItemDialog = async (item = null) => {
    console.debug("[AdminDashboard] Opening item dialog", item ? "for edit" : "for create");
    setItemErrors({});
    if (item) {
      setEditingItem(item);
      setItemFormData({
        name: item.name || "",
        description: item.description || "",
        price: item.price || "",
        category_id: item.category_id || "",
        subcategory_id: item.subcategory_id || "",
        image: null,
      });
    } else {
      setEditingItem(null);
      setItemFormData({ name: "", description: "", price: "", category_id: "", subcategory_id: "", image: null });
    }
    setItemDialogOpen(true);
  };

  const handleOpenCategoryDialog = (category = null) => {
    console.debug("[AdminDashboard] Opening category dialog", category ? "for edit" : "for create");
    setCategoryErrors({});
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({ name: category.name || "", icon: category.icon || "", sort_order: category.sort_order || "" });
    } else {
      setEditingCategory(null);
      setCategoryFormData({ name: "", icon: "", sort_order: "" });
    }
    setCategoryDialogOpen(true);
  };

  const handleOpenSubcategoryDialog = (subcategory = null) => {
    console.debug("[AdminDashboard] Opening subcategory dialog", subcategory ? "for edit" : "for create");
    setSubcategoryErrors({});
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setSubcategoryFormData({ name: subcategory.name || "", category_id: subcategory.category_id || "", image: null });
    } else {
      setEditingSubcategory(null);
      setSubcategoryFormData({ name: "", category_id: "", image: null });
    }
    setSubcategoryDialogOpen(true);
  };

  const handleItemSubmit = async () => {
    console.debug("[AdminDashboard] Submitting item form...", itemFormData);
    const errors = validateItem(itemFormData);
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      showSnackbar(Object.values(errors)[0], "error");
      return;
    }
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", itemFormData.name.trim());
      formDataToSend.append("description", itemFormData.description || "");
      formDataToSend.append("price", parseFloat(itemFormData.price));
      formDataToSend.append("category_id", itemFormData.category_id);
      if (itemFormData.subcategory_id) formDataToSend.append("subcategory_id", itemFormData.subcategory_id);
      if (itemFormData.image) formDataToSend.append("image", itemFormData.image);

      let response;
      if (editingItem) {
        console.debug("[AdminDashboard] Updating item:", editingItem.id);
        response = await axios.post(`${API_BASE_URL}/api_menu_items.php?id=${editingItem.id}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
          params: { _method: "PUT" },
        });
      } else {
        console.debug("[AdminDashboard] Creating new item");
        response = await axios.post(`${API_BASE_URL}/api_menu_items.php`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      console.debug("[AdminDashboard] Submit response:", response.data);
      if (response.data.success) {
        showSnackbar(editingItem ? "Item updated successfully" : "Item created successfully", "success");
        setItemDialogOpen(false);
        await fetchData();
        await fetchCategories();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("[AdminDashboard] Submit error:", error);
      showSnackbar(`Error: ${error.response?.data?.message || error.message || "Unknown error occurred"}`, "error");
    }
  };

  const handleCategorySubmit = async () => {
    console.debug("[AdminDashboard] Submitting category form...", categoryFormData);
    const errors = validateCategory(categoryFormData);
    if (Object.keys(errors).length > 0) {
      setCategoryErrors(errors);
      showSnackbar(Object.values(errors)[0], "error");
      return;
    }
    try {
      let response;
      const categoryData = {
        name: categoryFormData.name.trim(),
        icon: categoryFormData.icon || null,
        sort_order: categoryFormData.sort_order ? parseInt(categoryFormData.sort_order) : null,
      };

      if (editingCategory) {
        console.debug("[AdminDashboard] Updating category:", editingCategory.id);
        response = await axios.put(`${API_BASE_URL}/api_categories.php`, { ...categoryData, id: editingCategory.id });
      } else {
        console.debug("[AdminDashboard] Creating new category");
        const formData = new FormData();
        formData.append("name", categoryData.name);
        formData.append("icon", categoryData.icon);
        formData.append("sort_order", categoryData.sort_order);
        response = await axios.post(`${API_BASE_URL}/api_categories.php`, formData);
      }
      console.debug("[AdminDashboard] Category submit response:", response.data);
      if (response.data.success) {
        showSnackbar(editingCategory ? "Category updated successfully" : "Category created successfully", "success");
        setCategoryDialogOpen(false);
        await fetchCategories();
        await fetchData();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("[AdminDashboard] Category submit error:", error);
      showSnackbar(`Error: ${error.response?.data?.message || error.message}`, "error");
    }
  };

  const handleSubcategorySubmit = async () => {
    console.debug("[AdminDashboard] Submitting subcategory form...", subcategoryFormData);
    const errors = validateSubcategory(subcategoryFormData);
    if (Object.keys(errors).length > 0) {
      setSubcategoryErrors(errors);
      showSnackbar(Object.values(errors)[0], "error");
      return;
    }
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", subcategoryFormData.name.trim());
      formDataToSend.append("category_id", subcategoryFormData.category_id);
      if (subcategoryFormData.image) formDataToSend.append("image", subcategoryFormData.image);

      let response;
      if (editingSubcategory) {
        console.debug("[AdminDashboard] Updating subcategory:", editingSubcategory.id);
        response = await axios.put(`${API_BASE_URL}/api_subcategories.php`, {
          id: editingSubcategory.id,
          name: subcategoryFormData.name.trim(),
        });
      } else {
        console.debug("[AdminDashboard] Creating new subcategory");
        response = await axios.post(`${API_BASE_URL}/api_subcategories.php`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      console.debug("[AdminDashboard] Subcategory submit response:", response.data);
      if (response.data.success) {
        showSnackbar(editingSubcategory ? "Subcategory updated successfully" : "Subcategory created successfully", "success");
        setSubcategoryDialogOpen(false);
        await fetchCategories();
        await fetchData();
        await fetchAllSubcategories();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("[AdminDashboard] Subcategory submit error:", error);
      showSnackbar(`Error: ${error.response?.data?.message || error.message}`, "error");
    }
  };

  const handleDeleteItem = async (id, name) => {
    console.debug(`[AdminDashboard] Deleting item: ${id} - ${name}`);
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api_menu_items.php?id=${id}`);
        console.debug("[AdminDashboard] Delete response:", response.data);
        if (response.data.success) {
          showSnackbar("Item deleted successfully", "success");
          await fetchData();
        } else {
          showSnackbar(response.data.message || "Delete failed", "error");
        }
      } catch (error) {
        console.error("[AdminDashboard] Delete error:", error);
        showSnackbar("Error deleting item: " + (error.response?.data?.message || error.message), "error");
      }
    }
  };

  const handleDeleteCategory = async (id, name) => {
    console.debug(`[AdminDashboard] Deleting category: ${id} - ${name}`);
    if (window.confirm(`Are you sure you want to delete category "${name}"? This will also delete all subcategories and items in it.`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api_categories.php?id=${id}`);
        console.debug("[AdminDashboard] Delete category response:", response.data);
        if (response.data.success) {
          showSnackbar("Category deleted successfully", "success");
          await fetchCategories();
          await fetchData();
          await fetchAllSubcategories();
        } else {
          showSnackbar(response.data.message || "Delete failed", "error");
        }
      } catch (error) {
        console.error("[AdminDashboard] Delete category error:", error);
        showSnackbar("Error deleting category: " + (error.response?.data?.message || error.message), "error");
      }
    }
  };

  const handleDeleteSubcategory = async (id, name) => {
    console.debug(`[AdminDashboard] Deleting subcategory: ${id} - ${name}`);
    if (window.confirm(`Are you sure you want to delete subcategory "${name}"? This will also delete all items in it.`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api_subcategories.php?id=${id}`);
        console.debug("[AdminDashboard] Delete subcategory response:", response.data);
        if (response.data.success) {
          showSnackbar("Subcategory deleted successfully", "success");
          await fetchCategories();
          await fetchData();
          await fetchAllSubcategories();
        } else {
          showSnackbar(response.data.message || "Delete failed", "error");
        }
      } catch (error) {
        console.error("[AdminDashboard] Delete subcategory error:", error);
        showSnackbar("Error deleting subcategory: " + (error.response?.data?.message || error.message), "error");
      }
    }
  };

  const getAllItems = useCallback(() => {
    const items = [];
    if (!Array.isArray(menuData)) return items;
    menuData.forEach((category) => {
      if (category.items && Array.isArray(category.items)) {
        category.items.forEach((item) => {
          items.push({ ...item, category_name: category.name, subcategory_name: null, type: "direct" });
        });
      }
      if (category.subcategories && Array.isArray(category.subcategories)) {
        category.subcategories.forEach((subcategory) => {
          if (subcategory.items && Array.isArray(subcategory.items)) {
            subcategory.items.forEach((item) => {
              items.push({ ...item, category_name: category.name, subcategory_name: subcategory.name, type: "subcategory" });
            });
          }
        });
      }
    });
    return items;
  }, [menuData]);

  const getSubcategoryItems = useCallback(() => {
    const items = [];
    if (!Array.isArray(menuData)) return items;
    menuData.forEach((category) => {
      if (category.subcategories && Array.isArray(category.subcategories)) {
        category.subcategories.forEach((subcategory) => {
          items.push({
            id: subcategory.id,
            name: subcategory.name,
            category_id: category.id,
            category_name: category.name,
            image_url: subcategory.image_url,
            items_count: subcategory.items ? subcategory.items.length : 0,
          });
        });
      }
    });
    return items;
  }, [menuData]);

  const getFilteredItems = useCallback(() => {
    let items = getAllItems();
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        (item) =>
          item.name?.toLowerCase().includes(term) ||
          item.category_name?.toLowerCase().includes(term) ||
          (item.subcategory_name && item.subcategory_name.toLowerCase().includes(term)) ||
          item.description?.toLowerCase().includes(term)
      );
    }
    if (categoryFilter) items = items.filter((item) => item.category_id === parseInt(categoryFilter));
    if (subcategoryFilter) items = items.filter((item) => item.subcategory_id === parseInt(subcategoryFilter));
    if (priceRange.min) items = items.filter((item) => item.price >= parseFloat(priceRange.min));
    if (priceRange.max) items = items.filter((item) => item.price <= parseFloat(priceRange.max));
    return items;
  }, [getAllItems, searchTerm, categoryFilter, subcategoryFilter, priceRange]);

  const getFilteredCategories = useCallback(() => {
    if (!categorySearchTerm) return categories;
    const term = categorySearchTerm.toLowerCase();
    return categories.filter((cat) => cat.name?.toLowerCase().includes(term));
  }, [categories, categorySearchTerm]);

  const getFilteredSubcategories = useCallback(() => {
    if (!subcategorySearchTerm) return allSubcategories;
    const term = subcategorySearchTerm.toLowerCase();
    return allSubcategories.filter(
      (sub) => sub.name?.toLowerCase().includes(term) || sub.category_name?.toLowerCase().includes(term)
    );
  }, [allSubcategories, subcategorySearchTerm]);

  const filteredItems = getFilteredItems();
  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const filteredCategories = getFilteredCategories();
  const filteredSubcategoriesList = getFilteredSubcategories();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setSubcategoryFilter("");
    setPriceRange({ min: "", max: "" });
    setPage(0);
    showSnackbar("Filters cleared", "info");
  };

  if (loadingInitial) {
    return <Preloader onDone={() => {}} />;
  }

  /* -------------------- shared UI tokens -------------------- */
  const darkPanelSx = {
    bgcolor: "#211e19",
    border: `1px solid ${HAIRLINE}`,
    borderRadius: 0,
    p: { xs: 2.5, md: 3 },
  };
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      color: "#f6f1e9",
      bgcolor: "rgba(255,255,255,.02)",
      "& fieldset": { borderColor: "rgba(255,255,255,.18)" },
      "&:hover fieldset": { borderColor: "rgba(215,166,109,.55)" },
      "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: 1 },
    },
    "& .MuiInputLabel-root": { color: MUTED, fontSize: ".82rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
    "& .MuiFormHelperText-root": { color: MUTED, fontSize: ".7rem" },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": { color: MUTED },
  };
  const selectSx = {
    borderRadius: 0,
    color: "#f6f1e9",
    bgcolor: "rgba(255,255,255,.02)",
    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.18)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(215,166,109,.55)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
    ".MuiSvgIcon-root": { color: MUTED },
  };

  return (
    <>
      <Box sx={{ bgcolor: INK, minHeight: "100vh", color: "#f7f3ed" }}>
        <AdminHeader
          isMobile={isMobile}
          onRefresh={() => { fetchData(); fetchCategories(); fetchAllSubcategories(); }}
          onAddItem={() => handleOpenItemDialog()}
          onAddCategory={() => handleOpenCategoryDialog()}
          onAddSubcategory={() => handleOpenSubcategoryDialog()}
        />

        <Box sx={{ py: { xs: 4, md: 6 } }}>
          <Container maxWidth="xl">
            {/* Page heading */}
            <Box sx={{ mb: 4, display: "flex", alignItems: { xs: "flex-start", md: "end" }, justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography sx={{ color: ACCENT, letterSpacing: ".2em", fontSize: ".72rem", fontWeight: 700, mb: 1.5 }}>
                  MANAGE THE MENU
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: { xs: "2.4rem", md: "3.4rem" },
                    letterSpacing: "-.04em",
                    lineHeight: 1.05,
                    fontWeight: 400,
                  }}
                >
                  Admin dashboard
                </Typography>
                <Typography sx={{ color: MUTED, mt: 1.2 }}>
                  Manage your restaurant's menu items, categories, and subcategories.
                </Typography>
              </Box>

              {isMobile && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    onClick={() => { fetchData(); fetchCategories(); fetchAllSubcategories(); }}
                    startIcon={<RefreshIcon />}
                    sx={{ color: ACCENT, border: "1px solid rgba(215,166,109,.55)", borderRadius: 0, px: 2, py: 1, fontSize: ".8rem", textTransform: "none" }}
                  >
                    Refresh
                  </Button>
                  <Button
                    onClick={() => handleOpenItemDialog()}
                    startIcon={<AddIcon />}
                    sx={{ bgcolor: ACCENT, color: "#17120d", borderRadius: 0, px: 2, py: 1, fontSize: ".8rem", fontWeight: 700, textTransform: "none", "&:hover": { bgcolor: "#edc18b" } }}
                  >
                    Add
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: HAIRLINE, mb: 3 }} />

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                mb: 4,
                borderBottom: `1px solid ${HAIRLINE}`,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: ".9rem",
                  minWidth: { xs: "auto", sm: 180 },
                  color: MUTED,
                  px: { xs: 1.5, sm: 2 },
                },
                "& .Mui-selected": { color: `${ACCENT} !important` },
              }}
              TabIndicatorProps={{ sx: { backgroundColor: ACCENT, height: 2 } }}
            >
              <Tab icon={<MenuIcon />} iconPosition="start" label={`Menu Items (${filteredItems.length})`} />
              <Tab icon={<CategoryIcon />} iconPosition="start" label={`Categories (${filteredCategories.length})`} />
              <Tab icon={<SubcategoryIcon />} iconPosition="start" label={`Subcategories (${filteredSubcategoriesList.length})`} />
            </Tabs>

            {/* MENU ITEMS TAB */}
            {activeTab === 0 && (
              <Fade in={activeTab === 0} timeout={300}>
                <Box>
                  {/* Filters */}
                  <Box sx={{ ...darkPanelSx, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                      <FilterListIcon sx={{ color: ACCENT, fontSize: 18 }} />
                      <Typography sx={{ fontSize: ".68rem", letterSpacing: ".18em", color: MUTED, fontWeight: 600 }}>
                        FILTERS & SEARCH
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search by name, category, subcategory..."
                          value={searchTerm}
                          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                            endAdornment: searchTerm && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm("")}>
                                  <ClearIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Category Filter</InputLabel>
                          <Select
                            value={categoryFilter}
                            onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                            label="Category Filter"
                            sx={selectSx}
                          >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Subcategory Filter</InputLabel>
                          <Select
                            value={subcategoryFilter}
                            onChange={(e) => { setSubcategoryFilter(e.target.value); setPage(0); }}
                            label="Subcategory Filter"
                            sx={selectSx}
                          >
                            <MenuItem value="">All Subcategories</MenuItem>
                            {allSubcategories.map((sub) => (
                              <MenuItem key={sub.id} value={sub.id}>{sub.name} ({sub.category_name})</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Min Price (₦)"
                          value={priceRange.min}
                          onChange={(e) => setPriceRange((prev) => ({ ...prev, min: e.target.value }))}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Button
                          fullWidth
                          onClick={clearFilters}
                          sx={{
                            height: "40px",
                            borderRadius: 0,
                            border: "1px solid rgba(215,166,109,.55)",
                            color: ACCENT,
                            textTransform: "none",
                            fontSize: ".78rem",
                            "&:hover": { bgcolor: "rgba(215,166,109,.08)" },
                          }}
                        >
                          Clear
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Items Table */}
                  <Box sx={{ ...darkPanelSx, p: 0, overflow: "hidden" }}>
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table sx={{ minWidth: 800 }}>
                        <TableHead sx={{ bgcolor: "rgba(215,166,109,.06)" }}>
                          <TableRow>
                            {["ID", "Item Name", "Category", "Subcategory", "Price (₦)", "Description", "Image", "Actions"].map((h) => (
                              <TableCell
                                key={h}
                                align={h === "Actions" ? "center" : "left"}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: ".72rem",
                                  letterSpacing: ".1em",
                                  color: MUTED,
                                  borderBottom: `1px solid ${HAIRLINE}`,
                                }}
                              >
                                {h.toUpperCase()}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 8, borderBottom: "none" }}>
                                <InventoryIcon sx={{ fontSize: 48, color: MUTED, mb: 2 }} />
                                <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.3rem", color: "#f6f1e9" }}>
                                  No menu items found
                                </Typography>
                                <Typography sx={{ color: MUTED, fontSize: ".85rem", mt: 0.5 }}>
                                  Try adjusting your filters or add a new item.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedItems.map((item) => (
                              <TableRow
                                key={item.id}
                                hover
                                sx={{ "&:hover": { bgcolor: "rgba(215,166,109,.03)" }, "& td": { borderBottom: `1px solid ${HAIRLINE}` } }}
                              >
                                <TableCell sx={{ color: MUTED, fontSize: ".82rem" }}>{item.id}</TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: ".88rem", fontWeight: 600, color: "#f6f1e9" }}>{item.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={item.category_name}
                                    size="small"
                                    sx={{
                                      bgcolor: "rgba(215,166,109,.1)",
                                      color: ACCENT,
                                      border: "1px solid rgba(215,166,109,.35)",
                                      borderRadius: 0,
                                      fontSize: ".72rem",
                                      height: 22,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {item.subcategory_name ? (
                                    <Chip
                                      label={item.subcategory_name}
                                      size="small"
                                      sx={{
                                        bgcolor: "transparent",
                                        color: "#d2c7b8",
                                        border: `1px solid ${HAIRLINE}`,
                                        borderRadius: 0,
                                        fontSize: ".72rem",
                                        height: 22,
                                      }}
                                    />
                                  ) : (
                                    <Typography sx={{ color: MUTED, fontSize: ".8rem" }}>—</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: ".85rem", fontVariantNumeric: "tabular-nums" }}>
                                    ₦{parseFloat(item.price).toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={item.description || ""}>
                                    <Typography sx={{ fontSize: ".8rem", color: MUTED, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {item.description || "—"}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {item.image_url ? (
                                    <Avatar
                                      src={`${API_BASE_URL.replace("/api", "")}/uploads/${item.image_url}`}
                                      variant="rounded"
                                      sx={{ width: 42, height: 42, borderRadius: 1, border: `1px solid ${HAIRLINE}` }}
                                    >
                                      <RestaurantIcon sx={{ fontSize: 18, color: MUTED }} />
                                    </Avatar>
                                  ) : (
                                    <Typography sx={{ color: MUTED, fontSize: ".8rem" }}>—</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={() => handleOpenItemDialog(item)}
                                      size="small"
                                      sx={{ color: ACCENT, "&:hover": { bgcolor: "rgba(215,166,109,.12)" }, mr: 0.5 }}
                                    >
                                      <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      onClick={() => handleDeleteItem(item.id, item.name)}
                                      size="small"
                                      sx={{ color: "#e57373", "&:hover": { bgcolor: "rgba(229,115,115,.12)" } }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      component="div"
                      count={filteredItems.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      sx={{
                        color: MUTED,
                        borderTop: `1px solid ${HAIRLINE}`,
                        ".MuiSvgIcon-root": { color: MUTED },
                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { color: MUTED, fontSize: ".8rem" },
                      }}
                    />
                  </Box>
                </Box>
              </Fade>
            )}

            {/* CATEGORIES TAB */}
            {activeTab === 1 && (
              <Fade in={activeTab === 1} timeout={300}>
                <Box>
                  <Box sx={{ ...darkPanelSx, mb: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search categories by name..."
                      value={categorySearchTerm}
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                        endAdornment: categorySearchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setCategorySearchTerm("")}>
                              <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    {filteredCategories.length === 0 ? (
                      <Grid item xs={12}>
                        <Box sx={{ ...darkPanelSx, p: 8, textAlign: "center" }}>
                          <CategoryIcon sx={{ fontSize: 48, color: MUTED, mb: 2 }} />
                          <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.4rem", color: "#f6f1e9" }}>
                            No categories found
                          </Typography>
                        </Box>
                      </Grid>
                    ) : (
                      filteredCategories.map((category, index) => (
                        <Zoom in key={category.id} style={{ transitionDelay: `${index * 50}ms` }}>
                          <Grid item xs={12} md={6} lg={4}>
                            <Box
                              sx={{
                                ...darkPanelSx,
                                height: "100%",
                                transition: "border-color .2s ease, transform .2s ease",
                                "&:hover": { borderColor: "rgba(215,166,109,.45)", transform: "translateY(-3px)" },
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
                                  <Box
                                    sx={{
                                      width: 40, height: 40,
                                      display: "grid", placeItems: "center",
                                      border: "1px solid rgba(215,166,109,.45)",
                                      color: ACCENT,
                                    }}
                                  >
                                    <CategoryIcon sx={{ fontSize: 20 }} />
                                  </Box>
                                  <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.15rem", color: "#f6f1e9" }}>
                                    {category.name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenCategoryDialog(category)}
                                      sx={{ color: ACCENT, "&:hover": { bgcolor: "rgba(215,166,109,.12)" } }}
                                    >
                                      <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteCategory(category.id, category.name)}
                                      sx={{ color: "#e57373", "&:hover": { bgcolor: "rgba(229,115,115,.12)" } }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Divider sx={{ borderColor: HAIRLINE, my: 1.5 }} />
                              <Box sx={{ display: "grid", gap: 1 }}>
                                <Typography sx={{ fontSize: ".78rem", color: MUTED }}>
                                  <span style={{ color: "#e7e1d8", fontWeight: 600 }}>Sort order:</span> {category.sort_order || "Default"}
                                </Typography>
                                <Typography sx={{ fontSize: ".78rem", color: MUTED }}>
                                  <span style={{ color: "#e7e1d8", fontWeight: 600 }}>Icon:</span> {category.icon || "None"}
                                </Typography>
                              </Box>
                              <Chip
                                label={`ID: ${category.id}`}
                                size="small"
                                sx={{
                                  mt: 2,
                                  bgcolor: "rgba(215,166,109,.1)",
                                  color: ACCENT,
                                  border: "1px solid rgba(215,166,109,.35)",
                                  borderRadius: 0,
                                  fontSize: ".7rem",
                                  height: 22,
                                }}
                              />
                            </Box>
                          </Grid>
                        </Zoom>
                      ))
                    )}
                  </Grid>
                </Box>
              </Fade>
            )}

            {/* SUBCATEGORIES TAB */}
            {activeTab === 2 && (
              <Fade in={activeTab === 2} timeout={300}>
                <Box>
                  <Box sx={{ ...darkPanelSx, mb: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search subcategories by name or category..."
                      value={subcategorySearchTerm}
                      onChange={(e) => setSubcategorySearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                        endAdornment: subcategorySearchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSubcategorySearchTerm("")}>
                              <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    {filteredSubcategoriesList.length === 0 ? (
                      <Grid item xs={12}>
                        <Box sx={{ ...darkPanelSx, p: 8, textAlign: "center" }}>
                          <SubcategoryIcon sx={{ fontSize: 48, color: MUTED, mb: 2 }} />
                          <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.4rem", color: "#f6f1e9" }}>
                            No subcategories found
                          </Typography>
                          <Button
                            onClick={() => handleOpenSubcategoryDialog()}
                            startIcon={<AddIcon />}
                            sx={{
                              mt: 2,
                              bgcolor: ACCENT,
                              color: "#17120d",
                              borderRadius: 0,
                              px: 2.5,
                              py: 1,
                              fontSize: ".82rem",
                              fontWeight: 700,
                              textTransform: "none",
                              "&:hover": { bgcolor: "#edc18b" },
                            }}
                          >
                            Add Subcategory
                          </Button>
                        </Box>
                      </Grid>
                    ) : (
                      filteredSubcategoriesList.map((subcategory, index) => (
                        <Zoom in key={subcategory.id} style={{ transitionDelay: `${index * 50}ms` }}>
                          <Grid item xs={12} md={6} lg={4}>
                            <Box
                              sx={{
                                ...darkPanelSx,
                                height: "100%",
                                transition: "border-color .2s ease, transform .2s ease",
                                "&:hover": { borderColor: "rgba(215,166,109,.45)", transform: "translateY(-3px)" },
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
                                  <Box
                                    sx={{
                                      width: 40, height: 40,
                                      display: "grid", placeItems: "center",
                                      border: "1px solid rgba(215,166,109,.45)",
                                      color: ACCENT,
                                    }}
                                  >
                                    <SubcategoryIcon sx={{ fontSize: 20 }} />
                                  </Box>
                                  <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.15rem", color: "#f6f1e9" }}>
                                    {subcategory.name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenSubcategoryDialog(subcategory)}
                                      sx={{ color: ACCENT, "&:hover": { bgcolor: "rgba(215,166,109,.12)" } }}
                                    >
                                      <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteSubcategory(subcategory.id, subcategory.name)}
                                      sx={{ color: "#e57373", "&:hover": { bgcolor: "rgba(229,115,115,.12)" } }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Divider sx={{ borderColor: HAIRLINE, my: 1.5 }} />
                              <Box sx={{ display: "grid", gap: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                  <Typography sx={{ fontSize: ".78rem", color: MUTED }}>Category:</Typography>
                                  <Chip
                                    label={subcategory.category_name}
                                    size="small"
                                    sx={{
                                      bgcolor: "rgba(215,166,109,.1)",
                                      color: ACCENT,
                                      border: "1px solid rgba(215,166,109,.35)",
                                      borderRadius: 0,
                                      fontSize: ".7rem",
                                      height: 22,
                                    }}
                                  />
                                </Box>
                                <Typography sx={{ fontSize: ".78rem", color: MUTED }}>
                                  <span style={{ color: "#e7e1d8", fontWeight: 600 }}>Items:</span> {subcategory.items_count || 0}
                                </Typography>
                              </Box>
                              {subcategory.image_url && (
                                <Box sx={{ mt: 2 }}>
                                  <Avatar
                                    src={`${API_BASE_URL.replace("/api", "")}/uploads/${subcategory.image_url}`}
                                    variant="rounded"
                                    sx={{ width: 54, height: 54, borderRadius: 1, border: `1px solid ${HAIRLINE}` }}
                                  />
                                </Box>
                              )}
                              <Chip
                                label={`ID: ${subcategory.id}`}
                                size="small"
                                sx={{
                                  mt: 2,
                                  bgcolor: "rgba(215,166,109,.06)",
                                  color: MUTED,
                                  border: `1px solid ${HAIRLINE}`,
                                  borderRadius: 0,
                                  fontSize: ".7rem",
                                  height: 22,
                                }}
                              />
                            </Box>
                          </Grid>
                        </Zoom>
                      ))
                    )}
                  </Grid>
                </Box>
              </Fade>
            )}
          </Container>
        </Box>

        <AdminFooter />
      </Box>

      {/* =============== DIALOGS =============== */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#211e19", color: "#f7f3ed", borderRadius: 0, border: `1px solid ${HAIRLINE}` } }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${HAIRLINE}` }}>
          <Typography sx={{ color: ACCENT, letterSpacing: ".18em", fontSize: ".65rem", fontWeight: 700, mb: 0.5 }}>
            {editingItem ? "EDIT" : "NEW"}
          </Typography>
          <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.5rem", letterSpacing: "-.02em" }}>
            {editingItem ? "Edit menu item" : "Add menu item"}
          </Typography>
        </Box>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Item Name"
            name="name"
            value={itemFormData.name}
            onChange={handleItemInputChange}
            margin="normal"
            required
            autoFocus
            error={!!itemErrors.name}
            helperText={itemErrors.name}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <FormControl fullWidth margin="normal" required error={!!itemErrors.category_id} sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select name="category_id" value={itemFormData.category_id} onChange={handleCategoryChange} label="Category" sx={selectSx}>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
            {itemErrors.category_id && <FormHelperText>{itemErrors.category_id}</FormHelperText>}
          </FormControl>
          {itemFormData.category_id && (
            <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
              <InputLabel>Subcategory (Optional)</InputLabel>
              <Select
                name="subcategory_id"
                value={itemFormData.subcategory_id}
                onChange={handleItemInputChange}
                label="Subcategory (Optional)"
                sx={selectSx}
              >
                <MenuItem value="">None</MenuItem>
                {allSubcategories
                  .filter((sub) => sub.category_id === parseInt(itemFormData.category_id))
                  .map((sub) => (
                    <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            label="Price (₦)"
            name="price"
            type="number"
            value={itemFormData.price}
            onChange={handleItemInputChange}
            margin="normal"
            required
            error={!!itemErrors.price}
            helperText={itemErrors.price}
            InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={itemFormData.description}
            onChange={handleItemInputChange}
            margin="normal"
            multiline
            rows={3}
            error={!!itemErrors.description}
            helperText={itemErrors.description || `${itemFormData.description?.length || 0}/500 characters`}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <Button
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
            sx={{
              mt: 1,
              py: 1.2,
              borderRadius: 0,
              border: "1px dashed rgba(215,166,109,.5)",
              color: ACCENT,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(215,166,109,.06)" },
            }}
          >
            {itemFormData.image ? "Change image" : "Upload image"}
            <input type="file" name="image" accept="image/*" hidden onChange={handleItemInputChange} />
          </Button>
          {itemFormData.image && (
            <Typography sx={{ fontSize: ".75rem", color: "#a5d6a7", mt: 1 }}>
              Selected: {itemFormData.image.name}
            </Typography>
          )}
          {editingItem && editingItem.image_url && !itemFormData.image && (
            <Typography sx={{ fontSize: ".75rem", color: MUTED, mt: 1 }}>
              Current image: {editingItem.image_url}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${HAIRLINE}` }}>
          <Button onClick={() => setItemDialogOpen(false)} startIcon={<CloseIcon />} sx={{ color: MUTED, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleItemSubmit}
            startIcon={<SaveIcon />}
            sx={{
              bgcolor: ACCENT,
              color: "#17120d",
              borderRadius: 0,
              px: 2.5,
              py: 1,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "#edc18b" },
            }}
          >
            {editingItem ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#211e19", color: "#f7f3ed", borderRadius: 0, border: `1px solid ${HAIRLINE}` } }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${HAIRLINE}` }}>
          <Typography sx={{ color: ACCENT, letterSpacing: ".18em", fontSize: ".65rem", fontWeight: 700, mb: 0.5 }}>
            {editingCategory ? "EDIT" : "NEW"}
          </Typography>
          <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.5rem", letterSpacing: "-.02em" }}>
            {editingCategory ? "Edit category" : "Add category"}
          </Typography>
        </Box>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Category Name"
            name="name"
            value={categoryFormData.name}
            onChange={handleCategoryInputChange}
            margin="normal"
            required
            autoFocus
            error={!!categoryErrors.name}
            helperText={categoryErrors.name}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Icon (optional)"
            name="icon"
            value={categoryFormData.icon}
            onChange={handleCategoryInputChange}
            margin="normal"
            placeholder="e.g., Pizza, Burger, Drinks"
            helperText="Icon name or emoji for display"
            sx={{ ...fieldSx, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Sort Order (optional)"
            name="sort_order"
            type="number"
            value={categoryFormData.sort_order}
            onChange={handleCategoryInputChange}
            margin="normal"
            placeholder="Higher number appears first"
            helperText="Leave empty for default ordering"
            sx={fieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${HAIRLINE}` }}>
          <Button onClick={() => setCategoryDialogOpen(false)} startIcon={<CloseIcon />} sx={{ color: MUTED, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleCategorySubmit}
            startIcon={<SaveIcon />}
            sx={{
              bgcolor: ACCENT,
              color: "#17120d",
              borderRadius: 0,
              px: 2.5,
              py: 1,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "#edc18b" },
            }}
          >
            {editingCategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={subcategoryDialogOpen}
        onClose={() => setSubcategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#211e19", color: "#f7f3ed", borderRadius: 0, border: `1px solid ${HAIRLINE}` } }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${HAIRLINE}` }}>
          <Typography sx={{ color: ACCENT, letterSpacing: ".18em", fontSize: ".65rem", fontWeight: 700, mb: 0.5 }}>
            {editingSubcategory ? "EDIT" : "NEW"}
          </Typography>
          <Typography sx={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.5rem", letterSpacing: "-.02em" }}>
            {editingSubcategory ? "Edit subcategory" : "Add subcategory"}
          </Typography>
        </Box>
        <DialogContent sx={{ mt: 1 }}>
          <FormControl fullWidth margin="normal" required error={!!subcategoryErrors.category_id} sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category_id"
              value={subcategoryFormData.category_id}
              onChange={handleSubcategoryInputChange}
              label="Category"
              sx={selectSx}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
            {subcategoryErrors.category_id && <FormHelperText>{subcategoryErrors.category_id}</FormHelperText>}
          </FormControl>
          <TextField
            fullWidth
            label="Subcategory Name"
            name="name"
            value={subcategoryFormData.name}
            onChange={handleSubcategoryInputChange}
            margin="normal"
            required
            error={!!subcategoryErrors.name}
            helperText={subcategoryErrors.name}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <Button
            component="label"
            fullWidth
            startIcon={<ImageIcon />}
            sx={{
              mt: 1,
              py: 1.2,
              borderRadius: 0,
              border: "1px dashed rgba(215,166,109,.5)",
              color: ACCENT,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(215,166,109,.06)" },
            }}
          >
            {subcategoryFormData.image ? "Change image" : "Upload image (optional)"}
            <input type="file" name="image" accept="image/*" hidden onChange={handleSubcategoryInputChange} />
          </Button>
          {subcategoryFormData.image && (
            <Typography sx={{ fontSize: ".75rem", color: "#a5d6a7", mt: 1 }}>
              Selected: {subcategoryFormData.image.name}
            </Typography>
          )}
          {editingSubcategory && editingSubcategory.image_url && !subcategoryFormData.image && (
            <Typography sx={{ fontSize: ".75rem", color: MUTED, mt: 1 }}>
              Current image: {editingSubcategory.image_url}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${HAIRLINE}` }}>
          <Button onClick={() => setSubcategoryDialogOpen(false)} startIcon={<CloseIcon />} sx={{ color: MUTED, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubcategorySubmit}
            startIcon={<SaveIcon />}
            sx={{
              bgcolor: ACCENT,
              color: "#17120d",
              borderRadius: 0,
              px: 2.5,
              py: 1,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "#edc18b" },
            }}
          >
            {editingSubcategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{
            borderRadius: 0,
            bgcolor: snackbar.severity === "success" ? "#1b3a1e" : snackbar.severity === "error" ? "#3a1b1b" : "#2a2820",
            color: "#f6f1e9",
            border: `1px solid ${HAIRLINE}`,
            "& .MuiAlert-icon": { color: ACCENT },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminDashboard;